/**
 * GraphCompiler — converts serialized GraphData into a live Babylon FlowGraph.
 *
 * Walks the graph's nodes, instantiates blocks, wires data and flow connections,
 * and registers event blocks with the graph.
 *
 * TRACE_subgraph declarations are NOT inlined here. Each referenced definition
 * is compiled once into its own inner FlowGraph (`getOrCompileSubgraph`), and
 * every invocation becomes a `FlowGraphSubgraphBlock` that hands its inner
 * graph a fresh `FlowGraphContext` — so a subgraph is a closure with a private
 * variable scope, not an expansion. Flattening one into standard ops (for
 * export) is a separate, deliberate transform: see `subgraphInline.ts`.
 */

import { FlowGraphCoordinator } from "@babylonjs/core/FlowGraph/flowGraphCoordinator";
import type { FlowGraph } from "@babylonjs/core/FlowGraph/flowGraph";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphBlock } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { FlowGraphExecutionBlock } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlock";
import { FlowGraphEventBlock } from "@babylonjs/core/FlowGraph/flowGraphEventBlock";
import type { FlowGraphDataConnection } from "@babylonjs/core/FlowGraph/flowGraphDataConnection";
import type { Scene } from "@babylonjs/core/scene";
import { PointerInfo, PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import { PickingInfo } from "@babylonjs/core/Collisions/pickingInfo";
import type { AdvancedTimer } from "@babylonjs/core/Misc/timer";

import { FlowGraphReceiveCustomEventBlock } from "@babylonjs/core/FlowGraph/Blocks/Event/flowGraphReceiveCustomEventBlock";
import { FlowGraphSendCustomEventBlock } from "@babylonjs/core/FlowGraph/Blocks/Event/flowGraphSendCustomEventBlock";
import { FlowGraphBranchBlock } from "@babylonjs/core/FlowGraph/Blocks/Execution/ControlFlow/flowGraphBranchBlock";
import { FlowGraphSequenceBlock } from "@babylonjs/core/FlowGraph/Blocks/Execution/ControlFlow/flowGraphSequenceBlock";
import { FlowGraphSetDelayBlock } from "@babylonjs/core/FlowGraph/Blocks/Execution/ControlFlow/flowGraphSetDelayBlock";
import { FlowGraphCancelDelayBlock } from "@babylonjs/core/FlowGraph/Blocks/Execution/ControlFlow/flowGraphCancelDelayBlock";

import { Vector2, Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { GraphOps } from '../../scenedata/behaviors/behaviorgraphdata';
import type { GraphData, GraphNode, TypeDefinition } from '../../scenedata/behaviors/behaviorgraphdata';
import type { TimeTransition } from '../../scenedata/behaviors/propertiesdata';
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { LiveEventTarget } from './LiveEventTarget';

import {
    FlowGraphAddBlock, FlowGraphSubtractBlock, FlowGraphMultiplyBlock, FlowGraphDivideBlock,
    FlowGraphFloorBlock, FlowGraphRandomBlock,
    FlowGraphMinBlock, FlowGraphMaxBlock, FlowGraphPowerBlock, FlowGraphLessThanOrEqualBlock,
    FlowGraphEqualityBlock, FlowGraphGreaterThanBlock, FlowGraphGreaterThanOrEqualBlock, FlowGraphLessThanBlock,
    FlowGraphBitwiseNotBlock, FlowGraphBitwiseAndBlock, FlowGraphBitwiseOrBlock,
} from "@babylonjs/core/FlowGraph/Blocks/Data/Math/flowGraphMathBlocks";
import { FlowGraphCombineVector3Block } from "@babylonjs/core/FlowGraph/Blocks/Data/Math/flowGraphMathCombineExtractBlocks";
import { FlowGraphTypes } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { FlowGraphGetVariableBlock } from "@babylonjs/core/FlowGraph/Blocks/Data/flowGraphGetVariableBlock";
import { FlowGraphSetVariableBlock } from "@babylonjs/core/FlowGraph/Blocks/Execution/flowGraphSetVariableBlock";

import { TraceGetActiveBlock } from './blocks/TraceGetActiveBlock';
import { TraceSetBehaviorEnabledBlock } from './blocks/TraceSetBehaviorEnabledBlock';
import { TraceSetActiveBlock } from './blocks/TraceSetActiveBlock';
import { FlowGraphConditionalDataBlock } from "@babylonjs/core/FlowGraph/Blocks/Data/flowGraphConditionalDataBlock";
import { TracePlayAnimationBlock } from './blocks/TracePlayAnimationBlock';
import { TracePlayExternalBlock } from './blocks/TracePlayExternalBlock';
import { TracePlayVideoBlock } from './blocks/TracePlayVideoBlock';
import {
    TracePlayMediaBlock,
    TracePauseMediaBlock,
    TraceStopMediaBlock,
    TraceSeekMediaBlock,
    TraceSetMediaVolumeBlock,
    TraceIsMediaPlayingBlock,
    TraceGetMediaVolumeBlock,
} from './blocks/TraceMediaBlocks';
import { TraceNavigateBlock } from './blocks/TraceNavigateBlock';
import { TraceGetChildCountBlock, TraceSetActiveChildBlock } from './blocks/TraceChildBlocks';
import { TraceGetPropertyBlock } from './blocks/TraceGetPropertyBlock';
import { TraceSetPropertyBlock } from './blocks/TraceSetPropertyBlock';
import { TraceTimeBlock } from './blocks/TraceTimeBlock';
import { TraceSetStateBlock } from './blocks/TraceSetStateBlock';
import { TraceGetStateBlock } from './blocks/TraceGetStateBlock';
import { TraceOnStateChangeBlock } from './blocks/TraceOnStateChangeBlock';
import { FlowGraphMeshPickEventBlock } from "@babylonjs/core/FlowGraph/Blocks/Event/flowGraphMeshPickEventBlock";
import { FlowGraphPointerOverEventBlock } from "@babylonjs/core/FlowGraph/Blocks/Event/flowGraphPointerOverEventBlock";
import { FlowGraphPointerOutEventBlock } from "@babylonjs/core/FlowGraph/Blocks/Event/flowGraphPointerOutEventBlock";
import { TraceProximityEventBlock } from './blocks/TraceProximityEventBlock';
import { FlowGraphSceneTickEventBlock } from "@babylonjs/core/FlowGraph/Blocks/Event/flowGraphSceneTickEventBlock";
import { TracePagesNextBlock, TracePagesPrevBlock, TracePagesGetActiveIndexBlock, TracePagesGetPageCountBlock } from './blocks/TracePagesBlocks';
import { PointerSetBlock } from './blocks/PointerSetBlock';
import { PointerGetBlock } from './blocks/PointerGetBlock';
import { PointerInterpolateBlock } from './blocks/PointerInterpolateBlock';
import { FlowGraphSubgraphBlock, installExitSentinels } from './blocks/SubgraphBlock';
import type { IFlowGraphSubgraphInterface } from './blocks/SubgraphBlock';
import { setTraceContext } from './blocks/traceContext';
import { TraceInternalEventReceiveBlock, TraceInternalEventSendBlock } from './blocks/TraceInternalEventBlocks';

import { Logger } from '../../utils/Logger';
import { assertExecutableGraph } from './graphValidation';
import { getBlockDef } from './blockCatalog';

// ============================================================================
// Compiled graph result
// ============================================================================

export interface CompiledGraph {
    coordinator: FlowGraphCoordinator;
    graph: FlowGraph;
    context: FlowGraphContext;
    /** Fire a named custom event into the graph */
    fireEvent(eventId: string): void;
    /**
     * Simulate a pointer pick on the context object's mesh.
     * This triggers Babylon's native FlowGraphMeshPickEventBlock.
     */
    fireSelect(): void;
    /**
     * Simulate pointer hover enter on the context object's mesh.
     * This triggers Babylon's native FlowGraphPointerOverEventBlock.
     */
    fireHoverIn(): void;
    /**
     * Simulate pointer hover exit from the context object's mesh.
     * This triggers Babylon's native FlowGraphPointerOutEventBlock.
     */
    fireHoverOut(): void;
    /**
     * The objects a tap must land on (or under) to fire this graph's
     * onSelect events: each event's target, the host when it names none.
     * Recorded as the pick blocks are built, subgraphs included, so it is
     * exactly what runs — TraceScene uses it to tell which taps a graph
     * consumes. Read live: a target that does not resolve is left out, and
     * joins once an object is there.
     */
    readonly selectTargets: ReadonlySet<SceneObject>;
    /** Clean up */
    dispose(): void;
}

// ============================================================================
// Shared compilation state
// ============================================================================

interface CompileState {
    /** All instantiated blocks (flat, shared across all inlined subgraphs) */
    blocks: FlowGraphBlock[];
    context: FlowGraphContext;
    coordinator: FlowGraphCoordinator;
    scene: Scene;
    traceScene: TraceScene;
    contextObject: SceneObject;
    allGraphs: GraphData[];
    /** Tracks graph indices currently being compiled to detect cycles */
    compilingGraphs: Set<number>;
    /** Monotonic counter for unique variable prefixes per subgraph invocation */
    invocationCounter: number;
    /** Cache of compiled subgraph definitions (graph index → compiled inner FlowGraph + interface) */
    compiledSubgraphs: Map<number, CompiledSubgraph>;
    /** onSelect targets of every pick block built so far — see CompiledGraph.selectTargets */
    selectTargets: LiveEventTarget[];
}

/** A compiled subgraph definition, ready to be instantiated as SubgraphBlocks */
interface CompiledSubgraph {
    innerGraph: FlowGraph;
    subgraphInterface: IFlowGraphSubgraphInterface;
    /** Initialize a new inner context with default variable values and inline literals */
    initContext: (ctx: FlowGraphContext) => void;
}

// ============================================================================
// Public entry point
// ============================================================================

export function compileGraph(
    graphData: GraphData,
    scene: Scene,
    traceScene: TraceScene,
    contextObject: SceneObject,
    allGraphs?: GraphData[],
): CompiledGraph {
    assertExecutableGraph(graphData, allGraphs);
    const coordinator = new FlowGraphCoordinator({ scene });
    const graph = coordinator.createGraph();
    const context = graph.createContext();

    // Stash trace references so blocks can resolve SceneObjects at execution time
    setTraceContext(context, traceScene, contextObject);

    const graphs = allGraphs ?? [graphData];

    const state: CompileState = {
        blocks: [],
        context,
        coordinator,
        scene,
        traceScene,
        contextObject,
        allGraphs: graphs,
        compilingGraphs: new Set(),
        invocationCounter: 0,
        compiledSubgraphs: new Map(),
        selectTargets: [],
    };

    let mainIndex = graphs.indexOf(graphData);
    if (mainIndex === -1) {
        Logger.warn('[GraphCompiler] mainIndex: graphData not found in allGraphs, falling back to index 0');
        mainIndex = 0;
    }

    try {
        compileGraphInto(graphData, mainIndex, state, '');
    } catch (error) {
        coordinator.dispose();
        throw error;
    }

    // Register event blocks with the FlowGraph
    for (const block of state.blocks) {
        if (block instanceof FlowGraphEventBlock) {
            graph.addEventBlock(block);
        }
    }

    // Find a mesh on the context object for simulating pointer events in tests.
    // The native blocks use _IsDescendantOf to match, so any child mesh works.
    const contextMesh = contextObject.transform.getChildMeshes(false)[0] ?? null;

    return {
        coordinator,
        graph,
        context,
        get selectTargets() {
            const objects = new Set<SceneObject>();
            for (const target of state.selectTargets) {
                const object = target.object;
                if (object) objects.add(object);
            }
            return objects;
        },
        fireEvent(eventId: string) {
            coordinator.notifyCustomEvent(eventId, {});
        },
        fireSelect() {
            if (!contextMesh) return;
            const pickInfo = new PickingInfo();
            pickInfo.hit = true;
            pickInfo.pickedMesh = contextMesh;
            const pointerInfo = new PointerInfo(
                PointerEventTypes.POINTERPICK,
                // Synthetic event for graph-triggered picks; Babylon only reads standard pointer fields here.
                new PointerEvent('pointerdown'),
                pickInfo,
            );
            scene.onPointerObservable.notifyObservers(pointerInfo, PointerEventTypes.POINTERPICK);
        },
        fireHoverIn() {
            if (!contextMesh) return;
            scene.onMeshUnderPointerUpdatedObservable.notifyObservers(
                { mesh: contextMesh, pointerId: 0 },
                PointerEventTypes.POINTERMOVE,
            );
        },
        fireHoverOut() {
            // Notify with null mesh to trigger hover-out for the previously hovered mesh
            scene.onMeshUnderPointerUpdatedObservable.notifyObservers(
                { mesh: null, pointerId: 0 },
                PointerEventTypes.POINTERMOVE,
            );
        },
        dispose() {
            // Babylon 9.11.0's FlowGraphSetDelayBlock never registers itself
            // as a pending block (its `_execute` calls `_preparePendingTasks`
            // directly, not `_startPendingTasks`), so graph disposal's
            // _clearPendingBlocks() cannot see pending delay timers. Left
            // alone they keep riding scene.onBeforeRenderObservable and fire
            // into the disposed context — and a delay loop (the Interval
            // macro) would re-arm forever. Sweep the context's global
            // delay-timer registry first.
            const timers = context._getGlobalContextVariable<(AdvancedTimer | undefined)[]>('pendingDelays', []);
            for (const timer of timers) timer?.dispose();
            coordinator.dispose();
        },
    };
}

// ============================================================================
// Cycle detection guard
// ============================================================================

/** Returns false if the graph is already being compiled (cycle detected). Returns true if safe to proceed. */
function enterCompilation(state: CompileState, graphIndex: number): boolean {
    if (state.compilingGraphs.has(graphIndex)) {
        Logger.warn(`[GraphCompiler] Cycle detected: graph ${graphIndex} is already being compiled`);
        return false;
    }
    state.compilingGraphs.add(graphIndex);
    return true;
}

// ============================================================================
// Core: compile a single GraphData into the shared block array
// ============================================================================

function compileGraphInto(
    graphData: GraphData,
    graphIndex: number,
    state: CompileState,
    varPrefix: string,
): void {
    if (!enterCompilation(state, graphIndex)) return;

    const { context, contextObject, traceScene, allGraphs } = state;

    // Initialize variables (scoped per invocation via prefix). Resolve against
    // the declared type so float2/3/4 defaults become math instances — raw
    // arrays NaN through polymorphic math blocks (same policy as inline
    // literals, see resolveInlineLiteral).
    for (const variable of graphData.variables) {
        context.setVariable(varPrefix + variable.name, resolveInlineLiteral(variable, graphData.types));
    }

    // ── Phase 1: Instantiate blocks ──
    // SubgraphBlock nodes are regular blocks — no special mapping needed.
    const nodeToBlock: number[] = [];

    for (let i = 0; i < graphData.nodes.length; i++) {
        const gNode = graphData.nodes[i];
        const decl = graphData.declarations[gNode.declaration];
        if (!decl) {
            Logger.warn(`[GraphCompiler] Node ${i}: invalid declaration index ${gNode.declaration}`);
            nodeToBlock.push(state.blocks.length);
            state.blocks.push(failInvalidBlock());
            continue;
        }

        // Check for subgraph declaration
        const subRef = decl.extensions?.TRACE_subgraph;
        if (subRef) {
            if (subRef.asset != null) {
                Logger.warn(`[GraphCompiler] Node ${i}: external subgraph assets not yet supported (asset index ${subRef.asset})`);
            }
            const targetGraph = allGraphs[subRef.graph];
            if (!targetGraph) {
                Logger.warn(`[GraphCompiler] Node ${i}: subgraph references invalid graph index ${subRef.graph}`);
                nodeToBlock.push(state.blocks.length);
                state.blocks.push(failInvalidBlock());
                continue;
            }

            // Get or compile the subgraph definition (cached per graph index)
            const compiled = getOrCompileSubgraph(subRef.graph, targetGraph, state);
            if (!compiled) {
                nodeToBlock.push(state.blocks.length);
                state.blocks.push(failInvalidBlock());
                continue;
            }

            // Create a SubgraphBlock instance for this invocation
            const sgBlock = new FlowGraphSubgraphBlock({
                name: decl.op,
                subgraphInterface: compiled.subgraphInterface,
                innerGraph: compiled.innerGraph,
                initContext: compiled.initContext,
            });
            nodeToBlock.push(state.blocks.length);
            state.blocks.push(sgBlock);

            // No subgraphMappings entry needed — SubgraphBlock exposes its own
            // signal inputs/outputs and data connections that the parent wiring
            // phases handle like any normal block.
            continue;
        }

        const block = createBlock(decl.op, gNode, graphData, contextObject, traceScene, state.selectTargets, varPrefix);
        if (!block) {
            Logger.warn(`[GraphCompiler] Node ${i}: unknown op "${decl.op}"`);
            nodeToBlock.push(state.blocks.length);
            state.blocks.push(failInvalidBlock());
            continue;
        }
        nodeToBlock.push(state.blocks.length);
        assertBlockPorts(decl.op, block);
        state.blocks.push(block);
    }

    linkInternalEvents(graphData, state.blocks, nodeToBlock);

    // ── Phase 2: Wire data connections ──
    // SubgraphBlock nodes expose their interface sockets as normal data
    // inputs/outputs with the same names, so no special handling needed.
    for (let i = 0; i < graphData.nodes.length; i++) {
        const gNode = graphData.nodes[i];
        if (!gNode.values) continue;

        const decl = graphData.declarations[gNode.declaration];
        const op = decl?.op ?? '';
        // For subgraph nodes, socket names pass through (no remapping)
        const isSubgraphNode = !!decl?.extensions?.TRACE_subgraph;

        for (const [socketName, valueInput] of Object.entries(gNode.values)) {
            if (!('node' in valueInput)) {
                // Inline literal
                const targetBlock = state.blocks[nodeToBlock[i]];
                const inputName = isSubgraphNode ? socketName : mapDataInputName(op, socketName);
                const input = targetBlock.getDataInput(inputName);
                if (input) {
                    const val = resolveInlineLiteral(valueInput, graphData.types);
                    input.setValue(val, context);
                }
                continue;
            }

            // Reference to another node's output
            const srcNodeIdx = valueInput.node;
            const srcDecl = graphData.declarations[graphData.nodes[srcNodeIdx].declaration];
            const srcIsSubgraph = !!srcDecl?.extensions?.TRACE_subgraph;
            const srcOp = srcDecl?.op ?? '';

            const sourceBlock = state.blocks[nodeToBlock[srcNodeIdx]];
            const srcOutputName = srcIsSubgraph ? valueInput.socket : mapDataOutputName(srcOp, valueInput.socket);
            const sourceOutput = sourceBlock?.getDataOutput(srcOutputName);
            if (!sourceOutput) {
                Logger.warn(`[GraphCompiler] Cannot find output ${srcOutputName} on node ${srcNodeIdx}`);
                continue;
            }

            const targetBlock = state.blocks[nodeToBlock[i]];
            const tgtInputName = isSubgraphNode ? socketName : mapDataInputName(op, socketName);
            const targetInput = targetBlock.getDataInput(tgtInputName);
            if (targetInput) {
                sourceOutput.connectTo(targetInput);
            } else {
                Logger.warn(`[GraphCompiler] Cannot wire value: node ${srcNodeIdx}.${valueInput.socket} → node ${i}.${socketName}`);
            }
        }
    }

    // ── Phase 3: Wire flow connections ──
    // SubgraphBlock nodes expose interface flow sockets as normal signal
    // inputs/outputs with the same names, so no special handling needed.
    for (let i = 0; i < graphData.nodes.length; i++) {
        const gNode = graphData.nodes[i];
        if (!gNode.flows) continue;

        const decl = graphData.declarations[gNode.declaration];
        const op = decl?.op ?? '';
        const isSubgraphNode = !!decl?.extensions?.TRACE_subgraph;

        for (const [socketName, flowTarget] of Object.entries(gNode.flows)) {
            const srcBlockIdx = nodeToBlock[i];
            const srcSignal = isSubgraphNode ? socketName : mapFlowOutputName(op, socketName);

            const tgtDecl = graphData.declarations[graphData.nodes[flowTarget.node].declaration];
            const tgtIsSubgraph = !!tgtDecl?.extensions?.TRACE_subgraph;
            const tgtBlockIdx = nodeToBlock[flowTarget.node];
            const tgtSignal = tgtIsSubgraph ? flowTarget.socket : mapFlowInputName(flowTarget.socket);

            const sourceBlock = state.blocks[srcBlockIdx] as FlowGraphExecutionBlock;
            const targetBlock = state.blocks[tgtBlockIdx] as FlowGraphExecutionBlock;
            if (!sourceBlock || !targetBlock) continue;

            const srcSig = sourceBlock.getSignalOutput?.(srcSignal);
            const tgtSig = targetBlock.getSignalInput?.(tgtSignal);

            if (srcSig && tgtSig) {
                srcSig.connectTo(tgtSig);
            } else {
                Logger.warn(
                    `[GraphCompiler] Cannot wire flow: node ${i}.${socketName}(${srcSignal}) → node ${flowTarget.node}.${flowTarget.socket}(${tgtSignal})`
                );
            }
        }
    }

    state.compilingGraphs.delete(graphIndex);
}

/**
 * Point every internal `event/send` at the `event/receive` blocks of the same
 * event, in node order (the spec's activation order for multiple receivers).
 */
function linkInternalEvents(graphData: GraphData, blocks: readonly FlowGraphBlock[], nodeToBlock: readonly number[]): void {
    const receiversByEvent = new Map<number, TraceInternalEventReceiveBlock[]>();
    const senders: Array<{ block: TraceInternalEventSendBlock; event: number }> = [];
    graphData.nodes.forEach((node, i) => {
        const block = blocks[nodeToBlock[i]];
        const event = node.configuration ? configInt(node.configuration, 'event') : 0;
        if (block instanceof TraceInternalEventReceiveBlock) {
            let list = receiversByEvent.get(event);
            if (!list) receiversByEvent.set(event, (list = []));
            list.push(block);
        } else if (block instanceof TraceInternalEventSendBlock) {
            senders.push({ block, event });
        }
    });
    for (const { block, event } of senders) block.receivers = receiversByEvent.get(event) ?? [];
}

// ============================================================================
// Compile a subgraph definition into a reusable inner FlowGraph + interface
// ============================================================================

function getOrCompileSubgraph(
    graphIndex: number,
    subgraphData: GraphData,
    state: CompileState,
): CompiledSubgraph | null {
    // Return cached if already compiled
    const cached = state.compiledSubgraphs.get(graphIndex);
    if (cached) return cached;

    if (!enterCompilation(state, graphIndex)) return null;

    // Create a separate FlowGraph for this subgraph definition
    const innerGraph = state.coordinator.createGraph();
    const innerBlocks: FlowGraphBlock[] = [];

    // Compile the subgraph's nodes into the inner graph
    // We use a temporary state for the inner compilation
    const innerState: CompileState = {
        blocks: innerBlocks,
        context: innerGraph.createContext(),
        coordinator: state.coordinator,
        scene: state.scene,
        traceScene: state.traceScene,
        contextObject: state.contextObject,
        allGraphs: state.allGraphs,
        compilingGraphs: state.compilingGraphs,
        invocationCounter: state.invocationCounter,
        compiledSubgraphs: state.compiledSubgraphs,
        selectTargets: state.selectTargets,
    };

    // Initialize subgraph variables (typed defaults become math instances)
    for (const variable of subgraphData.variables) {
        innerState.context.setVariable(variable.name, resolveInlineLiteral(variable, subgraphData.types));
    }

    // Phase 1: Instantiate inner blocks
    const nodeToBlock: number[] = [];
    for (let i = 0; i < subgraphData.nodes.length; i++) {
        const gNode = subgraphData.nodes[i];
        const decl = subgraphData.declarations[gNode.declaration];
        if (!decl) {
            nodeToBlock.push(innerBlocks.length);
            innerBlocks.push(failInvalidBlock());
            continue;
        }

        // Nested subgraphs
        const nestedRef = decl.extensions?.TRACE_subgraph;
        if (nestedRef) {
            const nestedGraph = state.allGraphs[nestedRef.graph];
            if (nestedGraph) {
                const nestedCompiled = getOrCompileSubgraph(nestedRef.graph, nestedGraph, state);
                if (nestedCompiled) {
                    const nestedBlock = new FlowGraphSubgraphBlock({
                        name: decl.op,
                        subgraphInterface: nestedCompiled.subgraphInterface,
                        innerGraph: nestedCompiled.innerGraph,
                        initContext: nestedCompiled.initContext,
                    });
                    nodeToBlock.push(innerBlocks.length);
                    innerBlocks.push(nestedBlock);
                    continue;
                }
            }
            nodeToBlock.push(innerBlocks.length);
            innerBlocks.push(failInvalidBlock());
            continue;
        }

        const block = createBlock(decl.op, gNode, subgraphData, state.contextObject, state.traceScene, state.selectTargets, '');
        if (!block) {
            nodeToBlock.push(innerBlocks.length);
            innerBlocks.push(failInvalidBlock());
            continue;
        }
        nodeToBlock.push(innerBlocks.length);
        assertBlockPorts(decl.op, block);
        innerBlocks.push(block);
    }

    linkInternalEvents(subgraphData, innerBlocks, nodeToBlock);

    // Collect inline literal defaults so they can be applied to new inner contexts
    const inlineDefaults: Array<{ input: FlowGraphDataConnection<unknown>; value: unknown }> = [];

    // Phase 2: Wire data connections within the subgraph
    for (let i = 0; i < subgraphData.nodes.length; i++) {
        const gNode = subgraphData.nodes[i];
        if (!gNode.values) continue;
        const op = subgraphData.declarations[gNode.declaration]?.op ?? '';

        for (const [socketName, valueInput] of Object.entries(gNode.values)) {
            if (!('node' in valueInput)) {
                // Inline literal — set on compilation context AND record for future contexts
                const targetBlock = innerBlocks[nodeToBlock[i]];
                const input = targetBlock.getDataInput(mapDataInputName(op, socketName));
                if (input) {
                    const val = resolveInlineLiteral(valueInput, subgraphData.types);
                    input.setValue(val, innerState.context);
                    inlineDefaults.push({ input: input as FlowGraphDataConnection<unknown>, value: val });
                }
                continue;
            }
            const sourceBlock = innerBlocks[nodeToBlock[valueInput.node]];
            const targetBlock = innerBlocks[nodeToBlock[i]];
            if (!sourceBlock || !targetBlock) continue;
            const srcOp = subgraphData.declarations[subgraphData.nodes[valueInput.node].declaration]?.op ?? '';
            const sourceOutput = sourceBlock.getDataOutput(mapDataOutputName(srcOp, valueInput.socket));
            const targetInput = targetBlock.getDataInput(mapDataInputName(op, socketName));
            if (sourceOutput && targetInput) sourceOutput.connectTo(targetInput);
        }
    }

    // Phase 3: Wire flow connections within the subgraph
    for (let i = 0; i < subgraphData.nodes.length; i++) {
        const gNode = subgraphData.nodes[i];
        if (!gNode.flows) continue;
        const op = subgraphData.declarations[gNode.declaration]?.op ?? '';

        for (const [socketName, flowTarget] of Object.entries(gNode.flows)) {
            const sourceBlock = innerBlocks[nodeToBlock[i]] as FlowGraphExecutionBlock;
            const targetBlock = innerBlocks[nodeToBlock[flowTarget.node]] as FlowGraphExecutionBlock;
            if (!sourceBlock || !targetBlock) continue;
            const srcSig = sourceBlock.getSignalOutput?.(mapFlowOutputName(op, socketName));
            const tgtSig = targetBlock.getSignalInput?.(mapFlowInputName(flowTarget.socket));
            if (srcSig && tgtSig) srcSig.connectTo(tgtSig);
        }
    }

    // Register event blocks in the inner graph
    for (const block of innerBlocks) {
        if (block instanceof FlowGraphEventBlock) {
            innerGraph.addEventBlock(block);
        }
    }

    // Build the interface
    const iface = buildSubgraphInterface(subgraphData, innerBlocks, nodeToBlock);

    // Install exit sentinels
    installExitSentinels(iface.outputFlowSockets);

    state.compilingGraphs.delete(graphIndex);
    state.invocationCounter = innerState.invocationCounter;

    // Build context initializer: applies variable defaults and inline literal values
    const variableDecls = subgraphData.variables;
    const variableTypes = subgraphData.types;
    const capturedInlineDefaults = [...inlineDefaults];
    const { traceScene, contextObject } = state;
    const initContext = (ctx: FlowGraphContext) => {
        setTraceContext(ctx, traceScene, contextObject);
        for (const v of variableDecls) {
            // Resolve per context so typed defaults (Vector3 etc.) are fresh
            // instances — no mutable math object shared across invocations.
            ctx.setVariable(v.name, resolveInlineLiteral(v, variableTypes));
        }
        for (const d of capturedInlineDefaults) {
            d.input.setValue(d.value, ctx);
        }
    };

    const compiled: CompiledSubgraph = { innerGraph, subgraphInterface: iface, initContext };
    state.compiledSubgraphs.set(graphIndex, compiled);
    return compiled;
}

/**
 * Build an IFlowGraphSubgraphInterface from a compiled subgraph's blocks.
 */
function buildSubgraphInterface(
    subgraphData: GraphData,
    innerBlocks: FlowGraphBlock[],
    nodeToBlock: number[],
): IFlowGraphSubgraphInterface {
    const inputFlowSockets = new Map<string, { block: FlowGraphExecutionBlock; signalName: string }>();
    const outputFlowSockets = new Map<string, { block: FlowGraphExecutionBlock; signalName: string }>();
    const inputValueSockets = new Map<string, { connections: Array<{ block: FlowGraphBlock; inputName: string }>; variableName?: string }>();
    const outputValueSockets = new Map<string, { block: FlowGraphBlock; outputName: string }>();

    if (subgraphData.inputFlowSockets) {
        for (const [name, ref] of Object.entries(subgraphData.inputFlowSockets)) {
            const block = innerBlocks[nodeToBlock[ref.node]];
            if (block instanceof FlowGraphExecutionBlock) {
                inputFlowSockets.set(name, { block, signalName: mapFlowInputName(ref.socket) });
            }
        }
    }

    if (subgraphData.outputFlowSockets) {
        for (const [name, ref] of Object.entries(subgraphData.outputFlowSockets)) {
            const block = innerBlocks[nodeToBlock[ref.node]];
            const internalOp = subgraphData.declarations[subgraphData.nodes[ref.node]?.declaration]?.op ?? '';
            if (block instanceof FlowGraphExecutionBlock) {
                outputFlowSockets.set(name, { block, signalName: mapFlowOutputName(internalOp, ref.socket) });
            }
        }
    }

    if (subgraphData.inputValueSockets) {
        for (const [name] of Object.entries(subgraphData.inputValueSockets)) {
            // Find inner blocks that consume this input: unconnected data inputs matching the name
            const connections: Array<{ block: FlowGraphBlock; inputName: string }> = [];
            for (let ni = 0; ni < subgraphData.nodes.length; ni++) {
                const node = subgraphData.nodes[ni];
                const nodeDecl = subgraphData.declarations[node.declaration];
                if (!nodeDecl) continue;
                const block = innerBlocks[nodeToBlock[ni]];
                if (!block) continue;
                const mappedName = mapDataInputName(nodeDecl.op, name);
                const input = block.getDataInput(mappedName);
                // Include if the node has an inline value for this name, or an unconnected input
                if (input) {
                    const hasInlineValue = node.values && name in node.values && !('node' in node.values[name]);
                    const hasNoValue = !node.values || !(name in node.values);
                    if (hasInlineValue || hasNoValue) {
                        connections.push({ block, inputName: mappedName });
                    }
                }
            }
            inputValueSockets.set(name, { connections, variableName: name });
        }
    }

    if (subgraphData.outputValueSockets) {
        for (const [name, ref] of Object.entries(subgraphData.outputValueSockets)) {
            const block = innerBlocks[nodeToBlock[ref.node]];
            const internalOp = subgraphData.declarations[subgraphData.nodes[ref.node]?.declaration]?.op ?? '';
            if (block) {
                outputValueSockets.set(name, { block, outputName: mapDataOutputName(internalOp, ref.socket) });
            }
        }
    }

    return { inputFlowSockets, outputFlowSockets, inputValueSockets, outputValueSockets };
}

// ============================================================================
// Block factory
// ============================================================================

function createBlock(
    op: string,
    gNode: GraphNode,
    graphData: GraphData,
    contextObject: SceneObject,
    traceScene: TraceScene,
    selectTargets: LiveEventTarget[],
    varPrefix: string,
): FlowGraphBlock | null {
    const config = gNode.configuration ?? {};

    switch (op) {
        // ── Events (KHR_node_selectability / KHR_node_hoverability) ──
        // Empty target → the host object (graphs attached to an object).
        // Non-empty target → resolve the path; KHR_interactivity uses
        // `nodeIndex` config, our authoring format uses a transformPath. A path
        // that names nothing makes the trigger do nothing. The block reads its
        // target at each pointer event (LiveEventTarget), so an object created
        // after the graph compiled still works.
        case GraphOps.EVENT_ON_SELECT: {
            const target = eventTarget(config, contextObject, traceScene);
            selectTargets.push(target);
            const block = new FlowGraphMeshPickEventBlock({ targetMesh: target.mesh });
            target.follow(block.asset);
            return block;
        }

        case GraphOps.EVENT_ON_HOVER_IN: {
            const target = eventTarget(config, contextObject, traceScene);
            const block = new FlowGraphPointerOverEventBlock({ targetMesh: target.mesh });
            target.follow(block.targetMesh);
            return block;
        }

        case GraphOps.EVENT_ON_HOVER_OUT: {
            const target = eventTarget(config, contextObject, traceScene);
            const block = new FlowGraphPointerOutEventBlock({ targetMesh: target.mesh });
            target.follow(block.targetMesh);
            return block;
        }

        case GraphOps.TRACE_ON_PROXIMITY:
            return new TraceProximityEventBlock({
                target: configString(config, 'target'),
            });

        case GraphOps.EVENT_ON_START:
            return new FlowGraphReceiveCustomEventBlock({
                eventId: 'trace:start',
                eventData: {},
            });

        case GraphOps.EVENT_ON_TICK:
            return new FlowGraphSceneTickEventBlock();

        // ── Custom events ──
        // Internal events (no id) are scoped to the context and delivered on
        // the next frame, like Babylon's KHR loader — see
        // TraceInternalEventBlocks. An event with an external id goes through
        // the coordinator under that id, where a host environment can reach it.
        case GraphOps.EVENT_SEND:
        case GraphOps.EVENT_RECEIVE: {
            const eventIndex = configInt(config, 'event');
            const eventId = graphData.events[eventIndex]?.id ?? '';
            if (eventId === '') {
                return op === GraphOps.EVENT_SEND
                    ? new TraceInternalEventSendBlock(String(eventIndex))
                    : new TraceInternalEventReceiveBlock();
            }
            return op === GraphOps.EVENT_SEND
                ? new FlowGraphSendCustomEventBlock({ eventId, eventData: {} })
                : new FlowGraphReceiveCustomEventBlock({ eventId, eventData: {} });
        }

        // ── Pointer (KHR standard — transform read/write) ──
        case GraphOps.POINTER_SET:
            return new PointerSetBlock({
                target: configString(config, 'target'),
                property: configString(config, 'property'),
            });

        case GraphOps.POINTER_GET:
            return new PointerGetBlock({
                target: configString(config, 'target'),
                property: configString(config, 'property'),
            });

        case GraphOps.POINTER_INTERPOLATE:
            return new PointerInterpolateBlock({
                target: configString(config, 'target'),
                property: configString(config, 'property'),
            });

        // ── Trace actions ──
        case GraphOps.TRACE_SET_ACTIVE:
            return new TraceSetActiveBlock({ target: configString(config, 'target') });

        case GraphOps.TRACE_SET_BEHAVIOR_ENABLED:
            return new TraceSetBehaviorEnabledBlock({
                target: configString(config, 'target'),
                behavior: configString(config, 'behavior'),
            });

        case GraphOps.TRACE_GET_ACTIVE:
            return new TraceGetActiveBlock({ target: configString(config, 'target') });

        case GraphOps.TRACE_GET_CHILD_COUNT:
            return new TraceGetChildCountBlock({ target: configString(config, 'target') });

        case GraphOps.TRACE_SET_ACTIVE_CHILD:
            return new TraceSetActiveChildBlock({ target: configString(config, 'target') });

        case GraphOps.TRACE_GET_PROPERTY:
            return new TraceGetPropertyBlock({
                target: configString(config, 'target'),
                property: configString(config, 'property'),
            });

        case GraphOps.TRACE_TIME:
            return new TraceTimeBlock();

        case GraphOps.TRACE_SET_PROPERTY:
            return new TraceSetPropertyBlock({
                target: configString(config, 'target'),
                property: configString(config, 'property'),
                transition: configObject<TimeTransition>(config, 'transition'),
            });

        case GraphOps.TRACE_SET_STATE:
            return new TraceSetStateBlock({
                target: configString(config, 'target'),
            });

        case GraphOps.TRACE_GET_STATE:
            return new TraceGetStateBlock({
                target: configString(config, 'target'),
            });

        case GraphOps.TRACE_ON_STATE_CHANGE:
            return new TraceOnStateChangeBlock({
                target: configString(config, 'target'),
            });

        // ── Animation ──
        case GraphOps.TRACE_PLAY_ANIMATION:
            return new TracePlayAnimationBlock({
                target: configString(config, 'target'),
                clip: configString(config, 'clip') || undefined,
            });

        case GraphOps.TRACE_PLAY_ANIMATION_EXTERNAL:
            return new TracePlayExternalBlock({
                target: configString(config, 'target'),
                src: configString(config, 'src'),
                clip: configString(config, 'clip') || undefined,
            });

        // ── Pages ──
        case GraphOps.PAGES_NEXT:
            return new TracePagesNextBlock({ target: configString(config, 'target') });

        case GraphOps.PAGES_PREV:
            return new TracePagesPrevBlock({ target: configString(config, 'target') });

        case GraphOps.PAGES_GET_ACTIVE_INDEX:
            return new TracePagesGetActiveIndexBlock({ target: configString(config, 'target') });

        case GraphOps.PAGES_GET_PAGE_COUNT:
            return new TracePagesGetPageCountBlock({ target: configString(config, 'target') });

        // ── Navigate ──
        case GraphOps.TRACE_NAVIGATE:
            return new TraceNavigateBlock();

        // ── Video (deprecated, kept for back-compat) ──
        case GraphOps.TRACE_PLAY_VIDEO:
            return new TracePlayVideoBlock({
                target: configString(config, 'target'),
            });

        // ── Media (Video / AudioClip / Animation / Pages) ──
        case GraphOps.TRACE_PLAY_MEDIA:
            return new TracePlayMediaBlock({
                target: configString(config, 'target'),
            });
        case GraphOps.TRACE_PAUSE_MEDIA:
            return new TracePauseMediaBlock({
                target: configString(config, 'target'),
            });
        case GraphOps.TRACE_STOP_MEDIA:
            return new TraceStopMediaBlock({
                target: configString(config, 'target'),
            });
        case GraphOps.TRACE_SEEK_MEDIA:
            return new TraceSeekMediaBlock({
                target: configString(config, 'target'),
            });
        case GraphOps.TRACE_SET_MEDIA_VOLUME:
            return new TraceSetMediaVolumeBlock({
                target: configString(config, 'target'),
            });
        case GraphOps.TRACE_IS_MEDIA_PLAYING:
            return new TraceIsMediaPlayingBlock({
                target: configString(config, 'target'),
            });
        case GraphOps.TRACE_GET_MEDIA_VOLUME:
            return new TraceGetMediaVolumeBlock({
                target: configString(config, 'target'),
            });

        // ── Flow control ──
        case GraphOps.FLOW_BRANCH:
            return new FlowGraphBranchBlock();

        case GraphOps.FLOW_SEQUENCE: {
            // Sequence sockets are named by index. Size the block to the
            // highest wired index, not the key count — a partially wired
            // sequence (e.g. an Interval expansion whose payload socket '0'
            // has no chain attached yet) must not shift the others.
            const indices = gNode.flows ? Object.keys(gNode.flows).map(k => Number.parseInt(k, 10)) : [];
            const maxIndex = indices.reduce((m, i) => (Number.isNaN(i) ? m : Math.max(m, i)), -1);
            return new FlowGraphSequenceBlock({
                outputSignalCount: Math.max(2, maxIndex + 1),
            });
        }

        case GraphOps.FLOW_SET_DELAY:
            return new FlowGraphSetDelayBlock();

        case GraphOps.FLOW_CANCEL_DELAY:
            return new FlowGraphCancelDelayBlock();

        // ── Math/logic ──
        case GraphOps.MATH_NOT:
            return new FlowGraphBitwiseNotBlock({ valueType: FlowGraphTypes.Boolean });
        case GraphOps.MATH_AND:
            return new FlowGraphBitwiseAndBlock({ valueType: FlowGraphTypes.Boolean });
        case GraphOps.MATH_OR:
            return new FlowGraphBitwiseOrBlock({ valueType: FlowGraphTypes.Boolean });
        case GraphOps.MATH_EQ:
            return new FlowGraphEqualityBlock();
        case GraphOps.MATH_GT:
            return new FlowGraphGreaterThanBlock();
        case GraphOps.MATH_GE:
            return new FlowGraphGreaterThanOrEqualBlock();
        case GraphOps.MATH_LT:
            return new FlowGraphLessThanBlock();
        case GraphOps.MATH_ADD:
            return new FlowGraphAddBlock();
        case GraphOps.MATH_SUB:
            return new FlowGraphSubtractBlock();
        case GraphOps.MATH_MUL:
            return new FlowGraphMultiplyBlock();
        case GraphOps.MATH_DIV:
            return new FlowGraphDivideBlock();
        case GraphOps.MATH_SELECT:
            return new FlowGraphConditionalDataBlock();
        case GraphOps.MATH_MIN:
            return new FlowGraphMinBlock();
        case GraphOps.MATH_MAX:
            return new FlowGraphMaxBlock();
        case GraphOps.MATH_POW:
            return new FlowGraphPowerBlock();
        case GraphOps.MATH_LE:
            return new FlowGraphLessThanOrEqualBlock();
        case GraphOps.MATH_FLOOR:
            return new FlowGraphFloorBlock();
        case GraphOps.MATH_RANDOM:
            return new FlowGraphRandomBlock();
        case GraphOps.MATH_COMBINE3:
            return new FlowGraphCombineVector3Block();

        // ── Variables ──
        case GraphOps.VARIABLE_GET: {
            const varIndex = configInt(config, 'variable');
            const varName = graphData.variables[varIndex]?.name ?? `var_${varIndex}`;
            return new FlowGraphGetVariableBlock({ variable: varPrefix + varName });
        }
        case GraphOps.VARIABLE_SET: {
            const varIndex = configInt(config, 'variable');
            const varName = graphData.variables[varIndex]?.name ?? `var_${varIndex}`;
            return new FlowGraphSetVariableBlock({ variable: varPrefix + varName });
        }

        default:
            return null;
    }
}

// ============================================================================
// Helpers
// ============================================================================

/** The `target` config of a KHR_node_selectability/hoverability event; empty means the host (context) object. */
function eventTarget(
    config: Record<string, { type: number; value: unknown[] }>,
    contextObject: SceneObject,
    traceScene: TraceScene,
): LiveEventTarget {
    return new LiveEventTarget(configString(config, 'target'), contextObject, traceScene);
}

function configString(config: Record<string, { type: number; value: unknown[] }>, key: string): string {
    const cv = config[key];
    if (!cv || cv.value.length === 0) return '';
    return String(cv.value[0]);
}

function configInt(config: Record<string, { type: number; value: unknown[] }>, key: string): number {
    const cv = config[key];
    if (!cv || cv.value.length === 0) return 0;
    return Number(cv.value[0]) || 0;
}

/**
 * Read an object-valued config entry (e.g. a `transition` object). Config values
 * round-trip as JSON through GraphData, so an object survives verbatim; the KHR
 * `type` index is ignored for config addressing params (see GraphBuilder).
 */
function configObject<T>(config: Record<string, { type: number; value: unknown[] }>, key: string): T | undefined {
    const cv = config[key];
    if (!cv || cv.value.length === 0) return undefined;
    const v0 = cv.value[0];
    // Plain object only — reject null and arrays (an array-valued config would
    // otherwise slip through as a bogus object shape).
    return (v0 !== null && typeof v0 === 'object' && !Array.isArray(v0)) ? (v0 as T) : undefined;
}

function mapFlowOutputName(op: string, socketName: string): string {
    // These event blocks register their own output signals; the others fire the base class's `done`.
    if (op === GraphOps.TRACE_ON_PROXIMITY || op === GraphOps.TRACE_ON_STATE_CHANGE) {
        return socketName;
    }
    if (op.startsWith('trace/on') || op.startsWith('event/on')) {
        return 'done';
    }
    if (op === GraphOps.FLOW_BRANCH) {
        if (socketName === 'true') return 'onTrue';
        if (socketName === 'false') return 'onFalse';
    }
    if (op === GraphOps.FLOW_SEQUENCE) {
        return `out_${socketName}`;
    }
    if (op === GraphOps.FLOW_SET_DELAY && socketName === 'err') {
        // Babylon names the async error signal "error"; the KHR/spec socket is "err".
        return 'error';
    }
    return socketName;
}

function mapFlowInputName(socketName: string): string {
    // Remap hook for flow INPUTS. No current block diverges (only flow OUTPUTS
    // do — e.g. setDelay's err→error, branch's true→onTrue), so this is a
    // passthrough today; add cases here if a future block renames an input.
    return socketName;
}

// Shared by mapDataInputName and mapDataOutputName — a given op's socket names
// are unique across inputs and outputs, so one table serves both directions.
// The direction each entry applies to is noted inline (e.g. MATH_SELECT maps
// inputs `a`/`b` and output `value` in a single entry).
const DATA_SOCKET_REMAP: Record<string, Record<string, string>> = {
    [GraphOps.MATH_SELECT]: { a: 'onTrue', b: 'onFalse', value: 'output' }, // a,b: inputs; value: output
    [GraphOps.MATH_COMBINE3]: { a: 'input_0', b: 'input_1', c: 'input_2' }, // inputs
    // KHR uses an opaque `ref` socket (`lastDelay`/`delay`); Babylon implements it
    // as an integer index (`lastDelayIndex`/`delayIndex`). Map spec → Babylon names.
    [GraphOps.FLOW_SET_DELAY]: { lastDelay: 'lastDelayIndex' }, // output
    [GraphOps.FLOW_CANCEL_DELAY]: { delay: 'delayIndex' },      // input
};

/**
 * Resolve an inline literal (or variable default — same `{ type, value }`
 * shape) against its declared type signature.
 *
 * KHR stores literals as flat component arrays (`{ type, value: [x, y, z] }`).
 * Babylon's polymorphic math blocks dispatch on getClassName(), which raw
 * arrays don't have — both operands fall through to the scalar branch and the
 * result is NaN. Constructing the declared math type lets vector literals
 * survive math/* nodes. float4 builds a Quaternion: rotation is this engine's
 * only float4 producer/consumer (PointerGet/PointerSet), and quaternion
 * multiply — not component-wise Vector4 — is the meaningful composition.
 * Single-component and untyped values pass through unchanged.
 */
function resolveInlineLiteral(valueInput: { type: number; value: unknown[] }, types: TypeDefinition[]): unknown {
    // Accept the legacy nested vector encoding as well as KHR flat components.
    const raw = valueInput.value.length === 1 && Array.isArray(valueInput.value[0])
        ? valueInput.value[0] as unknown[] : valueInput.value;
    if (raw.length === 1) return raw[0];
    switch (types[valueInput.type]?.signature) {
        case 'float2': return Vector2.FromArray(raw as number[]);
        case 'float3': return Vector3.FromArray(raw as number[]);
        case 'float4': return Quaternion.FromArray(raw as number[]);
        default: return raw;
    }
}

function mapDataInputName(op: string, socketName: string): string {
    return DATA_SOCKET_REMAP[op]?.[socketName] ?? socketName;
}

function mapDataOutputName(op: string, socketName: string): string {
    return DATA_SOCKET_REMAP[op]?.[socketName] ?? socketName;
}

/** Catch catalog/runtime drift before starting any event or action. */
function assertBlockPorts(op: string, block: FlowGraphBlock): void {
    const def = getBlockDef(op);
    if (!def) return;
    const execution = block as FlowGraphExecutionBlock;
    const checks: [string, unknown][] = [
        ...def.dataInputs.map<[string, unknown]>(p => [p.name, block.getDataInput(mapDataInputName(op, p.name))]),
        ...def.dataOutputs.map<[string, unknown]>(p => [p.name, block.getDataOutput(mapDataOutputName(op, p.name))]),
        ...def.flowInputs.map<[string, unknown]>(p => [p, execution.getSignalInput?.(mapFlowInputName(p))]),
        ...def.flowOutputs.map<[string, unknown]>(p => [p, execution.getSignalOutput?.(mapFlowOutputName(op, p))]),
    ];
    for (const [socket, connection] of checks) if (!connection) throw new Error(`Native block catalog mismatch: ${op}.${socket} has no runtime connection`);
}

function failInvalidBlock(): FlowGraphBlock {
    throw new Error('Graph compilation failed: invalid block passed executable-graph validation.');
}
